import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';

import { DbUserModel } from '../../../../../shared/db-models/user';
import { ListViewComponent } from '../../components/list-view/list-view.component';
import {
  ListViewResults,
  ListViewSettings,
  ListViewTableColumnType,
} from '../../interfaces/list-view-models';
import { ServerResponse } from '../../interfaces/server-response';
import { ConnectedUserModel } from '../../interfaces/users-models';
import { AddAdminUserModalComponent } from '../../modals/add-admin-user-modal/add-admin-user-modal.component';
import { AppService } from '../../services/app.service';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
})
export class AdminUsersComponent implements OnDestroy {
  isLoading = true;

  connectedUser: ConnectedUserModel | null;
  usersService: UsersService;
  appService: AppService;
  modalService: NgbModal;
  toastr: ToastrService;

  listViewSettings: ListViewSettings = {
    allowCreate: true,
    allowEdit: false,
    allowGalleryMode: false,
    createButtonText: 'Add Admin User',
    isGalleryDefault: false,
    hideSearch: true,
    tableSettings: {
      columns: [
        {
          name: 'id',
          displayText: 'Id',
          columnType: ListViewTableColumnType.Text,
          allowSort: false,
        },
        {
          name: 'displayName',
          displayText: 'Display Name',
          columnType: ListViewTableColumnType.Text,
          allowSort: false,
        },
        {
          name: 'email',
          displayText: 'Email',
          columnType: ListViewTableColumnType.Text,
          allowSort: false,
        },
      ],
      showExpand: false,
    },
    defaultSortColumn: 'id',
    getData: () => this.getData(),
    create: () => this.create(),
    syncIntegrationData: () => {},
  };
  @ViewChild(ListViewComponent, { static: false }) private listView: ListViewComponent;

  private subsCleanup: Subscription[] = [];

  constructor() {
    this.usersService = inject(UsersService);
    this.appService = inject(AppService);
    this.modalService = inject(NgbModal);
    this.toastr = inject(ToastrService);
    this.connectedUser = this.usersService.getConnectedUserSync();
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    for (const currSub of this.subsCleanup) {
      currSub.unsubscribe();
    }
  }

  private getData(): Observable<ServerResponse<ListViewResults<DbUserModel>>> {
    const result = new Subject<ServerResponse<ListViewResults<DbUserModel>>>();

    // eslint-disable-next-line sonarjs/deprecation
    this.usersService.getAllSuperAdmins().subscribe(
      (users: DbUserModel[]) => {
        result.next({
          result: true,
          data: {
            data: users,
            dataCount: users.length,
          },
        });
        result.complete();
      },
      (err) => {
        result.error(err);
        result.complete();
      }
    );

    return result.asObservable();
  }

  private create(): Observable<boolean> {
    const createResult = new Subject<boolean>();

    const modal = this.modalService.open(AddAdminUserModalComponent, {
      size: 'lg',
      backdrop: 'static',
      windowClass: 'dark-modal',
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
    });

    // eslint-disable-next-line promise/catch-or-return
    modal.result
      .then(
        () => {
          createResult.next(true);
          return true;
        },
        () => {
          createResult.next(false);
          return false;
        }
      )
      .catch((error) => {
        createResult.next(false);
        console.error('Error creating admin user:', error);
        return false;
      })
      .finally(() => {
        createResult.complete();
      });

    return createResult.asObservable();
  }

  public removeUser(user: DbUserModel): void {
    this.appService
      .showConfirm('Confirm Delete', 'Are you sure you want to remove this user?')
      .subscribe((result: boolean) => {
        if (result) {
          // eslint-disable-next-line sonarjs/deprecation
          this.usersService.removeUserAsSuperAdmin(user.id).subscribe(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            (_: unknown) => {
              this.listView.refreshData();
            },
            (err) => {
              this.toastr.error(`Failed to remove the user.${err?.message ?? ''}`);
            }
          );
        }
      });
  }
}
